'use client';

import React, { useEffect, useState } from 'react';

import useDashboardsInvitations from '@/hooks/swr/useDashboardsInvitations';
import useUser from '@/hooks/swr/useUser';
import useDashboards from '@/utils/hooks/swr/useDashboards';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import useToggle from '@/hooks/utils/useToggle';
import { useRouter } from '@/utils/hooks/utils/useNewRouter';

import Icon from '@/components/base/Icon';
import Input from '@/components/base/Input';
import LoadMoreInfinite from '@/components/base/LoadMoreInfinite/LoadMoreInfinite';
import NotificationsCounter from '@/components/base/NotificationsCounter';
import SkeletonList from '@/components/base/Skeleton/SkeletonList';
import { Illustration } from '@/components/composite/Illustration';
import SelectGroupWidgetDashboardView from '@/components/pages/custom-dashboards/dashboard/select/SelectGroupWidgetDashboardView';
import SelectSpWidgetDashboardView from '@/components/pages/custom-dashboards/dashboard/select/SelectSPWidgetDashboardView';
import { CreateDashboardModal } from '@/components/pages/custom-dashboards/modals/CreateDashboardModal';
import DashboardInvitationList from '@/components/pages/custom-dashboards/modals/DashboardInvitationList';
import DashboardCard from '@/components/pages/custom-dashboards/overview/DashboardCard';
import DashboardEmpty from '@/components/pages/custom-dashboards/overview/DashboardsEmpty';

import illustrationEn from '../../../../../public/images/illustrations/illustrations-empty-en.webp';
import illustrationEs from '../../../../../public/images/illustrations/illustrations-empty-es.webp';
import illustrationFr from '../../../../../public/images/illustrations/illustrations-empty-fr.webp';

let clearId: ReturnType<typeof setTimeout>;

const gridClassName =
    'mt-12 grid gap-4 grid-cols-1 w-full @lg:grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4';

export default function DashboardOverviewPage() {
    const { t } = useTranslation('dashboard');

    const router = useRouter();
    const { user } = useUser();
    const { action } = router.query;
    const { invitations, total, pendingCount, mutateInvitations } = useDashboardsInvitations();

    const [group, setGroup] = useState<{ value?: string } | undefined>();
    const [searchTerm, setSearchTerm] = useState('');
    const [socialProfile, setSocialProfile] = useState<{ value?: string } | undefined>();
    const { isOpen: invitationOpen, toggle } = useToggle({
        initial: action === 'pending_invitations'
    });

    const { mutateDashboards, dashboards, loadMore, hasLoadMore, isLoadingMore, isLoading } =
        useDashboards({
            per_page: 12,
            search: searchTerm || undefined,
            social_profile: socialProfile?.value,
            group: group?.value
        });

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        clearTimeout(clearId);
        clearId = setTimeout(() => {
            setSearchTerm(e.target.value);
        }, 500);
    };

    useEffect(() => {
        if (!invitationOpen && action === 'pending_invitations') {
            router.replace(router.pathname);
        }
    }, [invitationOpen]);

    return (
        <>
            <header
                className="z-10 flex flex-wrap items-center gap-4 pb-5 sm:justify-between md:items-center md:pb-10"
                aria-label="Progress">
                <div className="font-GBold text-3.5xl">{t('dashboard_title')}</div>
                <div className="flex gap-3">
                    <CreateDashboardModal />
                    <button
                        onClick={toggle}
                        className="relative flex h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-white dark:bg-icoDarkMode-deepBlack"
                        title={t('dashboard_invitations')}>
                        <Icon
                            icon="envelope"
                            className="px-0 text-icoGray-500 dark:text-icoDarkMode-wolf"
                        />
                        {total > 0 ? (
                            <NotificationsCounter className="absolute right-[0.7rem] top-2">
                                {total}
                            </NotificationsCounter>
                        ) : null}
                    </button>
                    <DashboardInvitationList
                        invitations={invitations}
                        total={total}
                        pendingCount={pendingCount}
                        invitationOpen={invitationOpen}
                        mutateInvitations={mutateInvitations}
                        mutateDashboards={mutateDashboards}
                        toggle={toggle}
                    />
                </div>
            </header>
            <div className="flex flex-1 flex-col @container">
                {searchTerm === '' &&
                socialProfile?.value === undefined &&
                group?.value === undefined &&
                !dashboards?.length ? null : (
                    <div className="manipulation-header flex flex-wrap gap-4 font-GBold">
                        <Input
                            beforeInput={<Icon icon="search" />}
                            id="dashboard"
                            noBorderShadow
                            placeholder={t('placeholder_dashboard_search')}
                            onChange={handleSearch}
                            type="text"
                            noBorder
                            className="w-full lg:flex-1"
                        />
                        <div className="flex w-full flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0 lg:w-auto">
                            <div className="w-full lg:w-64">
                                <SelectGroupWidgetDashboardView
                                    selectedGroup={group || {}}
                                    setSelectedGroup={(group) => {
                                        setGroup(group.value === 'all' ? undefined : group);
                                    }}
                                    withAllLabel
                                />
                            </div>
                            <div className="w-full lg:w-64">
                                <SelectSpWidgetDashboardView
                                    selectedSP={socialProfile || {}}
                                    setSelectedSP={(profile) =>
                                        setSocialProfile(
                                            profile.value === 'all' ? undefined : profile
                                        )
                                    }
                                    withAllLabel
                                />
                            </div>
                        </div>
                    </div>
                )}

                {!isLoading ? (
                    dashboards?.length > 0 ? (
                        <div className={gridClassName}>
                            {dashboards.map((dashboard, index) => (
                                <React.Fragment key={index}>
                                    <DashboardCard
                                        index={index + 1}
                                        key={dashboard.id}
                                        dashboard={dashboard}
                                        user={user}
                                        mutateDashboards={mutateDashboards}
                                    />
                                    {index === dashboards.length - 1 && hasLoadMore ? (
                                        <div className="w-full grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4">
                                            <LoadMoreInfinite
                                                loadMore={loadMore}
                                                isLoadingMore={isLoadingMore}
                                            />
                                        </div>
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </div>
                    ) : searchTerm === '' &&
                      socialProfile?.value === undefined &&
                      group?.value === undefined ? (
                        <DashboardEmpty />
                    ) : (
                        <div className="relative flex flex-1 flex-col items-center justify-center space-y-2 text-icoGray-500 dark:text-icoDarkMode-wolf">
                            <Illustration
                                src={{ en: illustrationEn, fr: illustrationFr, es: illustrationEs }}
                            />

                            <span>{t('dashboard_empty')}</span>
                        </div>
                    )
                ) : (
                    <div className={gridClassName}>
                        <SkeletonList
                            count={8}
                            className="flex h-[18.125rem] w-full min-w-[15rem]"
                            type="square"
                        />
                    </div>
                )}
            </div>
        </>
    );
}
